import { Box, Checkbox, IconButton, Link, Tooltip, Typography, useTheme } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import React, { useContext, useEffect, useRef, useState } from 'react';
import CustomTable from './CustomTable';
import { getAllTeachers } from '../../api/content-api';
import SnackbarContext from '../../store/snackbar-context';
import Loader from '../../ui/Loader';
import Teacher from '../../model/Teacher';
import AuthContext from '../../store/auth-context';
import { useNavigate } from 'react-router-dom';
import { loginPage } from '../../store/fixed-routing';
import ApproveTeacherDialog from './ApproveTeacherDialog';
import OlympicsContext from '../../store/olympics-context';
import dayjs from 'dayjs';
import { filter } from './StagesTable';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { debugError } from '../../utils/debug';
import { MangedForm } from '../../ui/Form/MangedForm';
import TeacherEditDialog from './TeacherEditDialog';
import { useCustomTheme } from '../../store/theme-context';
import DisApproveTeacherDialog from './DisApproveTeacherDialog';
import CopyIconButton from './CopyIconButton';

const TeachersTable = () => {
	const [isLoading, setIsLoading] = useState(true);
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const navigate = useNavigate();
	const { setMsg } = useContext(SnackbarContext);
	const { logout, token, isAdmin } = useContext(AuthContext);
	const { olympics, districtFilterId, searchFilterValue, refreshTablesTrigger, refreshTables, setIsRefreshing } =
		useContext(OlympicsContext);
	const [teachers, setTeachers] = useState<Teacher[]>([]);
	const [displayedTeachers, setDisplayedTeachers] = useState<Teacher[]>([]);
	const [approveTeacher, setApproveTeacher] = useState<Teacher | null>();
	const [disapproveTeacher, setDisapproveTeacher] = useState<Teacher | null>();
	const editable = isAdmin || olympics?.registrationConfirmationFinishDate?.isAfter(dayjs());
	const [editedTeacherId, setEditedTeacherId] = useState<number | null>(null);
	const isRefreshingRef = useRef(false);

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}

		getAllTeachers(token)
			.then((response) => {
				if (response.status === 200) {
					const teachers = response.data.map((d: any) => Teacher.fromApiResponse(d));
					setTeachers(teachers);
				} else {
					debugError('TeachersTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	// Load data on mount or when refresh trigger changes
	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			// First load
			downloadData(false);
		} else {
			// Refresh triggered
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger]);

	useEffect(() => {
		setDisplayedTeachers(filterTeachers(teachers));
	}, [districtFilterId, searchFilterValue, teachers]);

	const filterTeachers = (list: Teacher[]) => {
		let filteredTeachers = filter(list, districtFilterId);
		filteredTeachers = filteredTeachers.filter((t) => {
			if (searchFilterValue !== null && searchFilterValue !== undefined && searchFilterValue !== '') {
				if (t.firstName.toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				if (t.lastName.toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				if (t.city.toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				if (t.street.toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				if (t.email.toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				if (t.phone.toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				if ((t.affiliation || '').toLowerCase().includes(searchFilterValue.toLowerCase())) {
					return true;
				}
				return false;
			}
			return true;
		});
		return filteredTeachers;
	};

	const renderContactDetails = (teacher: Teacher) => {
		const phoneDigits = teacher.phone ? teacher.phone.replace(/\D+/g, '') : '';
		const phone =
			phoneDigits.length === 9
				? `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`
				: teacher.phone;

		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
				{teacher.email && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
						<Link
							href={`mailto:${teacher.email}`}
							underline='hover'
							variant='caption'
						>
							{teacher.email}
						</Link>
						<CopyIconButton
							value={teacher.email}
							ariaLabel='Skopiuj email nauczyciela'
						/>
					</Box>
				)}
				{teacher.phone && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
						<Link
							href={`tel:${phoneDigits || teacher.phone}`}
							underline='hover'
							variant='caption'
						>
							{phone}
						</Link>
						<CopyIconButton
							value={phoneDigits || teacher.phone}
							ariaLabel='Skopiuj telefon nauczyciela'
						/>
					</Box>
				)}
			</Box>
		);
	};

	const data = (input: any) => {
		return {
			columns: [
				{ header: '', accessor: 'edit', width: '100px', nosort: true, editable: isAdmin },
				{ header: 'Potwierdzony', accessor: 'approved', align: 'center', width: '150px', editable: editable },
				{
					header: 'Email potwierdzony',
					accessor: 'emailVerified',
					align: 'center',
					width: '150px',
					editable: isAdmin,
				},
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				{ header: 'Stopień naukowy', accessor: 'academicDegree', align: 'left' },
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Drugie Imię', accessor: 'secondName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
				{ header: 'Afiliacja', accessor: 'affiliation', align: 'left' },
				{ header: 'Adres', accessor: 'address', align: 'left' },
				{ header: 'Dane kontaktowe', accessor: 'contactDetails', align: 'left' },
				{ header: 'Okręg', accessor: 'district', align: 'left' },
			],

			rows: input
				? input.map((i: Teacher) => {
						return {
							approved: (
								<Checkbox
									checked={i.approved}
									onChange={i.approved ? (event) => setDisapproveTeacher(i) : (event) => setApproveTeacher(i)}
								/>
							),
							emailVerified: (
								<Checkbox
									checked={i.emailVerified}
									disabled
								/>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							academicDegree: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.academicDegree}
								</Typography>
							),
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							secondName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.secondName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							address: (
								<Typography variant='caption'>
									{`${i.city}, ${i.zipCode}, ${i.street} ${i.building} ${i.apartment !== '' ? '/' + i.apartment : ''}`}
								</Typography>
							),
							contactDetails: renderContactDetails(i),
							affiliation: <Typography variant='caption'>{i.affiliation}</Typography>,
							district: <Typography variant='caption'>{i.district?.name ? i.district?.name : 'Nieznany'}</Typography>,
							edit: (
								<Tooltip title={'Edytuj'}>
									<IconButton
										onClick={() => setEditedTeacherId(i.wpUserId)}
										sx={{
											padding: 0,
											width: '100%',
											color: isHighContrast ? palette.primary.light : 'inherit',
										}}
									>
										<ModeEditIcon fontSize={'small'} />
									</IconButton>
								</Tooltip>
							),
						};
					})
				: [],
		};
	};

	return (
		<>
			{isLoading ? <Loader /> : <CustomTable data={data(displayedTeachers)} />}
			<ApproveTeacherDialog
				teacher={approveTeacher}
				close={() => setApproveTeacher(null)}
				onSuccess={() => {
					setApproveTeacher(null);
					downloadData(true); // Silent refresh
				}}
			/>
			<DisApproveTeacherDialog
				teacher={disapproveTeacher}
				close={() => setDisapproveTeacher(null)}
				onSuccess={() => {
					setDisapproveTeacher(null);
					downloadData(true); // Silent refresh
				}}
			/>
			<MangedForm>
				{editedTeacherId && (
					<TeacherEditDialog
						teacherWpId={editedTeacherId}
						open={!!editedTeacherId}
						onClose={() => setEditedTeacherId(null)}
						onSuccess={() => {
							setEditedTeacherId(null);
							refreshTables(); // Silent refresh after edit
						}}
					/>
				)}
			</MangedForm>
		</>
	);
};

export default TeachersTable;
